import React, { useRef } from 'react';
import { Check, Clock, ShieldCheck, Map, BookOpen, Layout, Globe2, Leaf, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { MetaPixel } from '../components/MetaPixel';
import { PurchasePopup } from '../components/PurchasePopup';
import { Testimonials } from '../components/Testimonials';
import { Bonuses } from '../components/Bonuses';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/nvSGSoEhbsxkFnof3MkcN4/PROYECTOS_Anuales_(7).png';
const CHECKOUT_URL = 'https://checkout11.edulabs.online/cart/47301990252770:1';
export function SalesPage() {
  const purchaseRef = useRef<HTMLDivElement>(null);
  const scrollToPurchase = () => {
    purchaseRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-white font-sans text-gray-900 overflow-x-hidden" data-id="element-44">
      <MetaPixel data-id="element-45" />
      <PurchasePopup data-id="element-46" />

      {/* HERO SECTION */}
      <header className="bg-white text-gray-900 pt-3 pb-10 px-4 text-center" data-id="element-47">
        <div className="max-w-4xl mx-auto" data-id="element-48">
          <div className="inline-block bg-blue-500 text-white font-bold px-4 py-1 rounded-full mb-2 text-sm uppercase tracking-wider" data-id="element-49">
            Para Docentes de Geografía
          </div>

          <h1 className="text-4xl md:text-6xl font-extrabold mb-1 leading-tight text-gray-900" data-id="element-50">
            🌍 MEGAPACK GEOGRAFÍA LISTA
          </h1>

          <div className="mb-1 cursor-pointer" onClick={scrollToPurchase} data-id="element-51">
            <img src={PRODUCT_IMAGE} alt="Megapack Geografía Lista" className="w-full max-w-[280px] mx-auto" data-id="element-52" />
          </div>

          <h2 className="text-2xl md:text-3xl font-medium text-gray-700 mb-3 max-w-2xl mx-auto" data-id="element-53">
            Entrá al aula con todo resuelto.
          </h2>

          <p className="text-lg md:text-xl text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-54">
            Clases listas, claras y aplicables para nivel medio, sin volver a
            planificar desde cero.
            <br data-id="element-55" />
            <span className="text-blue-600 font-bold mt-2 block" data-id="element-56">
              📚 Material anual listo para usar y adaptar
            </span>
          </p>

          <Button onClick={scrollToPurchase} size="xl" data-id="element-57">
            QUIERO MIS CLASES LISTAS
          </Button>
        </div>
      </header>

      {/* PROBLEM SECTION */}
      <section className="py-16 px-4 bg-sky-50" data-id="element-58">
        <div className="max-w-3xl mx-auto" data-id="element-59">
          <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-12" data-id="element-60">
            ⏳ Mientras preparás tus clases, el tiempo se te escapa…
          </h3>

          <div className="space-y-6 text-lg text-gray-700 bg-white p-8 rounded-2xl shadow-sm" data-id="element-61">
            <p className="font-medium" data-id="element-62">
              Y la sensación de no llegar nunca se repite semana tras semana.
            </p>

            <ul className="space-y-4" data-id="element-63">
              <li className="flex items-start gap-3" data-id="element-64">
                <span className="text-red-500 text-xl" data-id="element-65">🧠</span>
                <span data-id="element-66">Pasás horas armando clases desde cero</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-67">
                <span className="text-red-500 text-xl" data-id="element-68">🔁</span>
                <span data-id="element-69">Cuando terminás, ya estás pensando en la siguiente</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-70">
                <span className="text-red-500 text-xl" data-id="element-71">😓</span>
                <span data-id="element-72">Trabajás mucho, pero sentís que nunca alcanza</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-73">
                <span className="text-red-500 text-xl" data-id="element-74">📚</span>
                <span data-id="element-75">Llegás al aula cansado/a antes de empezar</span>
              </li>
            </ul>

            <div className="mt-8 p-4 bg-red-50 text-red-800 rounded-lg border-l-4 border-red-500" data-id="element-76">
              <p className="font-bold" data-id="element-77">
                No es falta de vocación. No es falta de ganas. Es falta de un
                sistema claro y material listo.
              </p>
            </div>

            <p className="text-gray-600 italic text-center mt-4" data-id="element-78">
              ⚠️ Cuando no tenés un sistema, repetís el mismo ciclo cada semana.
              <br data-id="element-79" />
              🔄 Planificar sin recursos listos te obliga a empezar de cero todo
              el tiempo.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-80">
        <div className="max-w-5xl mx-auto" data-id="element-81">
          <div className="text-center mb-16" data-id="element-82">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-83">
              🌍 Por eso creamos el MEGAPACK GEOGRAFÍA LISTA
            </h2>
            <p className="text-xl text-gray-600" data-id="element-84">
              Un sistema para dejar de improvisar y entrar al aula con todo
              resuelto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-85">
            <div className="bg-white p-6 rounded-xl border-2 border-blue-100 shadow-sm" data-id="element-86">
              <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mb-4" data-id="element-87">
                <BookOpen className="text-blue-600 w-6 h-6" data-id="element-88" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-89">
                📘 Todo el año organizado
              </h3>
              <p className="text-gray-600" data-id="element-90">
                6 libros digitales listos para nivel medio.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-blue-100 shadow-sm" data-id="element-91">
              <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mb-4" data-id="element-92">
                <Layout className="text-blue-600 w-6 h-6" data-id="element-93" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-94">
                🧩 Clases listas para aplicar
              </h3>
              <p className="text-gray-600" data-id="element-95">
                Actividades que ya funcionan, sin empezar de cero.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-blue-100 shadow-sm" data-id="element-96">
              <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mb-4" data-id="element-97">
                <Map className="text-blue-600 w-6 h-6" data-id="element-98" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-99">
                🗺️ Contenidos claros y visuales
              </h3>
              <p className="text-gray-600" data-id="element-100">
                Teoría simple, mapas e imágenes que ayudan a explicar mejor.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-blue-100 shadow-sm" data-id="element-101">
              <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mb-4" data-id="element-102">
                <Clock className="text-blue-600 w-6 h-6" data-id="element-103" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-104">
                ⏱️ Más tiempo y tranquilidad
              </h3>
              <p className="text-gray-600" data-id="element-105">
                Ahorrás semanas de trabajo y enseñás con seguridad.
              </p>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-106">
            <p className="text-2xl text-blue-600 mb-8 font-medium" data-id="element-107">
              ✨ Menos estrés. Más orden. Más disfrute al enseñar.
            </p>
          </div>
        </div>
      </section>

      {/* OFFER BANNER */}
      <section className="bg-sky-50 py-8 px-4" data-id="element-108">
        <div className="max-w-4xl mx-auto text-center" data-id="element-109">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2 uppercase tracking-tight" data-id="element-110">
            🎉 OFERTA POR TIEMPO LIMITADO
          </h2>
          <p className="text-lg font-medium text-gray-700 mb-6" data-id="element-111">
            ⏳ Estás a un paso de acceder al Megapack Geografía Lista, con todo
            el material listo para usar.
            <br data-id="element-112" />
            📌 Solo por tiempo limitado — transformá tus clases hoy.
          </p>
          <Button onClick={scrollToPurchase} size="lg" data-id="element-113">
            SI, QUIERO LA OFERTA AHORA
          </Button>
        </div>
      </section>

      {/* PRODUCT CONTENTS */}
      <section className="py-16 px-4 bg-white" data-id="element-114">
        <div className="max-w-4xl mx-auto" data-id="element-115">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4" data-id="element-116">
            📦 ¿Qué incluye el Megapack Geografía Lista?
          </h2>
          <p className="text-center text-gray-600 mb-12" data-id="element-117">
            Todo el material listo para usar en nivel medio.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-118">
            {[{
            icon: Globe2,
            title: 'Libro 1',
            desc: 'Orientación espacial, mapas, puntos cardinales y actividades dinámicas.',
            color: 'text-blue-600'
          }, {
            icon: Leaf,
            title: 'Libro 2',
            desc: 'Relieve, climas, paisajes naturales y actividades prácticas.',
            color: 'text-green-600'
          }, {
            icon: TrendingUp,
            title: 'Libro 3',
            desc: 'Población, migraciones, recursos, gráficos y análisis guiado.',
            color: 'text-orange-600'
          }, {
            icon: Globe2,
            title: 'Libro 4',
            desc: 'Globalización, economía mundial, regiones y proyectos.',
            color: 'text-red-600'
          }, {
            icon: Leaf,
            title: 'Libro 5',
            desc: 'Ambiente, desarrollo sostenible, geopolítica y casos reales.',
            color: 'text-blue-600'
          }, {
            icon: TrendingUp,
            title: 'Libro 6',
            desc: 'Economía, producción y análisis territorial.',
            color: 'text-purple-600'
          }].map((book, idx) => <div key={idx} className="flex items-start gap-4 p-4 rounded-lg bg-white border border-gray-200" data-id="element-119">
                <book.icon className={`w-8 h-8 flex-shrink-0 ${book.color}`} data-id="element-120" />
                <div data-id="element-121">
                  <h3 className={`font-bold text-lg ${book.color}`} data-id="element-122">
                    {book.title}
                  </h3>
                  <p className="text-gray-700" data-id="element-123">{book.desc}</p>
                </div>
              </div>)}
          </div>

          <div className="mt-12 bg-sky-50 p-6 rounded-xl text-center space-y-3" data-id="element-124">
            <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8 text-lg font-medium text-gray-800" data-id="element-125">
              <span className="flex items-center justify-center gap-2" data-id="element-126">
                <Check className="text-green-500" data-id="element-127" /> Formato PDF listo para
                imprimir
              </span>
              <span className="flex items-center justify-center gap-2" data-id="element-128">
                <Check className="text-green-500" data-id="element-129" /> Acceso inmediato · Uso
                ilimitado
              </span>
            </div>
            <p className="text-blue-600 font-bold pt-2" data-id="element-130">
              💡 Ahorrá tiempo de preparación y llevá tus clases de Geografía a
              un nivel superior 🚀
            </p>
          </div>
        </div>
      </section>

      {/* WHY SECTION */}
      <section className="py-16 px-4 bg-sky-50" data-id="element-131">
        <div className="max-w-5xl mx-auto" data-id="element-132">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900" data-id="element-133">
            🌍 ¿Por qué todos quieren este Megapack Geografía Lista?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-134">
            <div className="bg-white p-8 rounded-xl shadow-sm" data-id="element-135">
              <h3 className="text-xl font-bold text-blue-600 mb-6 border-b-2 border-blue-100 pb-2" data-id="element-136">
                📘 ¿Qué vas a recibir?
              </h3>
              <ul className="space-y-4" data-id="element-137">
                {['6 libros digitales completos', 'Unidades organizadas y claras', 'Actividades que ya funcionan', 'Recursos visuales y gráficos', 'Acceso inmediato'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-138">
                    <Check className="text-blue-500 flex-shrink-0" data-id="element-139" />
                    <span className="text-gray-700" data-id="element-140">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm" data-id="element-141">
              <h3 className="text-xl font-bold text-blue-600 mb-6 border-b-2 border-blue-100 pb-2" data-id="element-142">
                👨‍🏫 ¿Qué vas a lograr como docente?
              </h3>
              <ul className="space-y-4" data-id="element-143">
                {[{
                icon: '⏱️',
                text: 'Ahorrar horas de preparación'
              }, {
                icon: '✅',
                text: 'Enseñar con más seguridad y claridad'
              }, {
                icon: '📘',
                text: 'Cubrir todo el programa de Geografía de nivel medio'
              }, {
                icon: '✨',
                text: 'Diferenciarte con clases más claras y modernas'
              }, {
                icon: '🌱',
                text: 'Disfrutar más de enseñar'
              }].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-144">
                    <span className="text-xl" data-id="element-145">{item.icon}</span>
                    <span className="text-gray-700" data-id="element-146">{item.text}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <Bonuses onScrollToPurchase={scrollToPurchase} data-id="element-147" />

      {/* PURCHASE SECTION */}
      <section ref={purchaseRef} id="purchase-section" className="py-20 px-4 bg-white" data-id="element-148">
        <div className="max-w-3xl mx-auto text-center" data-id="element-149">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-blue-500 relative" data-id="element-150">
            {/* Badge */}
            <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-4 py-2 rounded-bl-xl z-10" data-id="element-151">
              OFERTA ESPECIAL
            </div>

            <div className="p-8 md:p-12" data-id="element-152">
              <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-153">
                Megapack Geografía Lista
              </h2>
              <p className="text-gray-500 mb-8" data-id="element-154">+ Todos los bonos de regalo</p>

              <div className="mb-2" data-id="element-155">
                <span className="text-xl text-gray-400 line-through decoration-red-500 decoration-2" data-id="element-156">
                  $47 USD
                </span>
              </div>

              <div className="mb-2" data-id="element-157">
                <span className="text-6xl font-black text-blue-600 tracking-tight" data-id="element-158">
                  $13.99{' '}
                  <span className="text-xl font-medium text-gray-500" data-id="element-159">USD</span>
                </span>
              </div>

              <p className="text-sm text-gray-500 mb-8" data-id="element-160">
                *Pagarás en la moneda de tu país
              </p>

              <Button href={CHECKOUT_URL} fullWidth size="xl" className="mb-6" data-id="element-161">
                SI, QUIERO LA OFERTA AHORA
              </Button>

              <div className="flex flex-col gap-2 text-sm text-gray-500 mb-8" data-id="element-162">
                <p className="flex items-center justify-center gap-2" data-id="element-163">
                  <ShieldCheck className="w-4 h-4 text-green-500" data-id="element-164" /> Pago 100%
                  Seguro
                </p>
                <p className="flex items-center justify-center gap-2" data-id="element-165">
                  <Check className="w-4 h-4 text-green-500" data-id="element-166" /> Acceso Inmediato
                  por Email
                </p>
              </div>

              {/* Guarantee */}
              <div className="bg-sky-50 rounded-xl p-6 border border-blue-100" data-id="element-167">
                <div className="flex flex-col items-center" data-id="element-168">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3" data-id="element-169">
                    <ShieldCheck className="w-6 h-6 text-blue-600" data-id="element-170" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-171">
                    🛡️ Garantía sin vueltas
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-172">
                    Probalo sin riesgo durante 7 días. Si no te convence, te
                    devolvemos el 100% de tu dinero.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials data-id="element-173" />

      {/* FAQ SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-174">
        <div className="max-w-3xl mx-auto" data-id="element-175">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-176">
            ❓ Preguntas Frecuentes
          </h2>

          <div className="space-y-4" data-id="element-177">
            {[{
            q: '¿Puedo usarlo en clases presenciales y online?',
            a: 'Sí, funciona para ambos formatos.'
          }, {
            q: '¿Es digital o físico?',
            a: '100% digital.'
          }, {
            q: '¿Cómo recibo el material?',
            a: 'Descarga inmediata luego de la compra.'
          }, {
            q: '¿Necesito experiencia previa?',
            a: 'No.'
          }, {
            q: '¿Es seguro comprar?',
            a: 'Sí.'
          }, {
            q: '¿Cuánto tiempo tengo acceso?',
            a: 'Acceso de por vida.'
          }].map((faq, i) => <div key={i} className="border-b border-gray-200 pb-4" data-id="element-178">
                <h3 className="font-bold text-lg text-gray-900 mb-2" data-id="element-179">
                  {faq.q}
                </h3>
                <p className="text-gray-600" data-id="element-180">{faq.a}</p>
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-181">
            <Button onClick={scrollToPurchase} data-id="element-182">ACCEDER AHORA</Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center text-sm" data-id="element-183">
        <p className="mb-4" data-id="element-184">
          © 2024 Megapack Geografía Lista. Todos los derechos reservados.
        </p>
        <p data-id="element-185">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
          Además, este sitio no está respaldado por Facebook de ninguna manera.
        </p>
      </footer>
    </div>;
}